import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment';

import { withFlags } from '../../../flags/Flags';

import { Combobox, Filter, Popover } from '@salesforce/design-system-react';
import rangeOptionValues from '../../../common/constants/dateRangeOptionValues';
import { DateRange } from '../../../common/components';
import { lastXDays } from '../../../common/utilities/dateRangeHelper';

class AssetCustomFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPopoverOpen: false,
      comboboxInProgress: true,
      propertySelection: [],
      predicateSelection: undefined
    };
  }

  /**
   * Selection object for the currently selected property.
   * This is an array of 0 or 1 elements
   */
  propertySelection() {
    if (this.state.propertySelection.length) {
      return this.state.propertySelection;
    }

    if (this.props.filter.property) {
      return [this.optionForAttribute(this.props.filter.property)];
    }

    return [];
  }

  /**
   * Selection object for the currently selected predicate (aka value)
   * This is an array of 0 or 1 elements
   */
  predicateSelection() {
    const propertySelection = this.propertySelection();
    if (propertySelection.length) {
      const property = propertySelection[0];
      const predicate = this.state.predicateSelection;

      switch (property.type) {
        case 'required':
          switch (property.id) {
            case 'availability':
            case 'fallback':
            case 'archived':
              if (predicate && predicate.length) {
                return predicate;
              }
              if (this.props.filter.predicate) {
                return [this.booleanOption(this.props.filter.predicate)];
              }
              return [this.booleanOption('Y')];
            case 'start_datetime':
            case 'end_datetime':
              // date ranges have two selections (from date and to date) so just return the obj
              if (predicate) {
                return predicate;
              }
              if (this.props.filter.predicate) {
                return this.props.filter.predicate;
              }
              return lastXDays(7).dates;
            default:
              break;
          }
          break;
        case 'custom':
          if (predicate && predicate.length) {
            return predicate;
          }
          if (this.props.filter.predicate) {
            return [this.optionForAttribute(this.props.filter.predicate)];
          }
          return [];
        default:
          break;
      }
    }
  }

  attributeType(id) {
    switch (id) {
      case 'availability':
      case 'fallback':
      case 'start_datetime':
      case 'end_datetime':
      case 'archived':
        return 'required';
      case 'required':
      case 'custom':
        return 'separator';
      default:
        return 'custom';
    }
  }

  optionForAttribute(id) {
    const { formatMessage } = this.props.intl;
    const type = this.attributeType(id);
    let label;

    if (id) {
      if (typeof id === 'string') {
        if (id.startsWith('attribute.')) {
          const attributes = Object.entries(this.props.attributes).flatMap(
            e => e[1]
          );
          const attribute = attributes.find(
            attr => `attribute.${attr.uuid}` === id
          );
          return this.optionForAttribute(attribute);
        } else {
          return { id, label: id, type };
        }
      } else if (type === 'custom') {
        return { id: `attribute.${id.uuid}`, label: id.name, type };
      }
      label = formatMessage({ id: `components.ecs.assetFilter.${id}` });
    }
    return { id, label, type };
  }

  booleanOption(id) {
    const { formatMessage } = this.props.intl;

    const label = formatMessage({ id: `components.ecs.assetFilter.${id}` });
    return { id, label };
  }

  /**
   * Returns an array of attribute options based on attribute options that
   * don't currently have filters.  Also includes a "separator" header so that
   * if any items are returned they will be grouped under a separator labeled
   * "required"
   */
  requiredAttributeOptions(flags) {
    const separator = {
      ...this.optionForAttribute('required'),
      type: 'separator'
    };
    const filters = [
      'availability',
      'fallback',
      'start_datetime',
      'end_datetime',
      'archived'
    ];
    const options = filters.map(this.optionForAttribute.bind(this));

    // All the current custom filters
    const usedOptions = this.props.customFilters.map(
      filter =>
        filter.property &&
        filter.type !== 'custom' &&
        filter.property.toLowerCase()
    );
    const unusedOptions = options.filter(opt => {
      // Remove any filter that is currently used (unless it's this filter)
      return (
        this.props.filter.property === opt.id ||
        !usedOptions.includes(opt.id.toLowerCase())
      );
    });
    if (unusedOptions.length) {
      return [separator, ...unusedOptions];
    } else {
      return [];
    }
  }

  customAttributeOptions(type, attributes) {
    const { formatMessage } = this.props.intl;
    const additionalLabel = formatMessage({
      id: `components.ecs.assetFilter.${type}`
    });

    const usedOptions = this.props.customFilters.map(
      filter =>
        filter.property &&
        filter.type === 'custom' &&
        filter.property.toLowerCase()
    );

    if (attributes?.length) {
      const separator = {
        id: type,
        label: additionalLabel,
        type: 'separator'
      };

      const customAttributes = attributes
        .map(this.optionForAttribute.bind(this))
        .filter(opt => {
          return (
            opt.id.trim().length > 0 &&
            (this.props.filter.property === opt.id ||
              !usedOptions.includes(opt.id.toLowerCase()))
          );
        });

      if (customAttributes.length) {
        return [separator, ...customAttributes];
      }
    }

    return [];
  }

  /**
   * Returns an array of all attribute options (required and custom)
   */
  attributeOptions(flags) {
    const weatherAttributes = Object.entries(this.props.attributes)
      .filter(e => e[0].toLowerCase().startsWith('weather'))
      .flatMap(e => e[1]);
    return [
      ...this.requiredAttributeOptions(flags),
      ...this.customAttributeOptions('weatherAttributes', weatherAttributes),
      ...this.customAttributeOptions(
        'additionalAttributes',
        this.props.attributes.assetAttributes
      )
    ];
  }

  /**
   * Returns a combobox for all availability attributes that can be filtered on
   * @returns {*}
   */
  attributeCombobox(flags) {
    let isOpen;
    let onRequestOpen;
    let onRequestClose;
    if (this.props.inProgress) {
      isOpen = this.state.comboboxInProgress;
      onRequestClose = () => {
        this.setState({ comboboxInProgress: false });
      };
      onRequestOpen = () => {
        this.setState({ comboboxInProgress: true });
      };
    }

    const { formatMessage } = this.props.intl;
    const fieldLabel = formatMessage({
      id: 'components.ecs.assetFilter.field'
    });
    const placeholder = formatMessage({
      id: 'components.ecs.assetFilter.placeholder'
    });

    return (
      <Combobox
        events={{
          onSelect: (event, { selection }) => {
            if (selection.length) {
              this.setState({
                propertySelection: selection,
                comboboxInProgress: false
              });
            } else {
              this.setState({ comboboxInProgress: false });
            }
          },
          onRequestClose,
          onRequestOpen
        }}
        classNameMenu="filter-popover"
        isOpen={isOpen}
        labels={{ label: fieldLabel, placeholderReadOnly: placeholder }}
        menuPosition="overflowBoundaryElement"
        readOnlyMenuItemVisibleLength={10}
        options={this.attributeOptions(flags)}
        selection={this.propertySelection()}
        variant="readonly"
      />
    );
  }

  /**
   * Returns a combobox with choices Y or N
   */
  booleanCombobox() {
    const { formatMessage } = this.props.intl;

    const optionTrue = this.booleanOption('Y');
    const optionFalse = this.booleanOption('N');

    const label = formatMessage({ id: 'components.ecs.assetFilter.value' });

    return (
      <Combobox
        events={{
          onSelect: (event, { selection }) => {
            this.setState({ predicateSelection: selection });
          }
        }}
        menuPosition="overflowBoundaryElement"
        labels={{ label }}
        options={[optionTrue, optionFalse]}
        selection={this.predicateSelection()}
        variant="readonly"
      />
    );
  }

  handleDateRangeChange(selection) {
    if (selection.selectedDate) {
      const { selectedRangeOption } = selection;
      this.setState({
        predicateSelection: {
          ...selection.selectedDate,
          selectedRangeOption
        }
      });
    }
  }

  /**
   * Returns an appropriate combobox with a date range picker
   */
  dateRangeCombobox() {
    const { formatMessage } = this.props.intl;
    const predicate = this.predicateSelection();
    let startDate;
    let endDate;

    if (predicate.selectedRangeOption) {
      if (predicate.startDate) {
        startDate = predicate.startDate;
      }
      if (predicate.endDate) {
        endDate = predicate.endDate;
      }
    }

    const property = this.propertySelection().length
      ? this.propertySelection()[0].value
      : undefined;

    return (
      <DateRange
        key={`date-range-for-${property}`}
        label={formatMessage({
          id: 'components.ecs.analytics.filter.daterRangeLabel'
        })}
        startDate={startDate}
        endDate={endDate}
        selectedRange={
          predicate.selectedRangeOption || rangeOptionValues.LAST_7_DAYS
        }
        onDateRangeChange={this.handleDateRangeChange.bind(this)}
        includeFuture={true}
      />
    );
  }

  /**
   * Returns an appropriate combobox for a required attribute
   */
  requiredAttributeCombobox(property) {
    switch (property.id) {
      case 'availability':
      case 'fallback':
      case 'archived':
        return this.booleanCombobox();
      case 'start_datetime':
        return this.dateRangeCombobox();
      case 'end_datetime':
        return this.dateRangeCombobox();
      default:
        console.warn('unexpected attribute type', property.id);
    }
  }

  customAttributeCombobox(property) {
    const { formatMessage } = this.props.intl;
    const label = formatMessage({ id: 'components.ecs.assetFilter.value' });
    const placeholder = formatMessage({
      id: 'components.ecs.assetFilter.valuePlaceholder'
    });
    const empty = formatMessage({ id: 'components.ecs.assetFilter.empty' });

    const attributes = Object.entries(this.props.attributes).flatMap(e => e[1]);
    const attribute = attributes.find(
      attr => `attribute.${attr.uuid}` === property.id
    );

    let options = [];
    if (attribute) {
      options = (attribute.values || []).map(value => ({
        id: value,
        label: value
      }));
      options.unshift({ id: '(empty)', label: empty });
    }

    return (
      <Combobox
        events={{
          onSelect: (event, { selection }) => {
            this.setState({ predicateSelection: selection });
          }
        }}
        menuPosition="overflowBoundaryElement"
        labels={{ label, placeholderReadOnly: placeholder }}
        options={options}
        selection={this.predicateSelection()}
        variant="readonly"
      />
    );
  }

  /**
   * Returns an appropriate combobox based on the selected property
   */
  attributeValueCombobox() {
    const propertySelection = this.propertySelection();
    if (propertySelection.length) {
      const property = propertySelection[0];
      switch (property.type) {
        case 'required':
          return this.requiredAttributeCombobox(property);
        case 'custom':
          return this.customAttributeCombobox(property);
        default:
          console.warn('Unexpected attribute value type', property.type);
          break;
      }
    }
  }

  /**
   * popoverBody provides a popover component just to pass props to the
   * inner popover of the filter
   */
  popoverBody() {
    let isOpen;
    let onRequestClose;
    if (this.props.inProgress) {
      isOpen = true;
      onRequestClose = this.props.onRemove;
    } else if (this.state.isPopoverOpen) {
      isOpen = true;
      onRequestClose = () => this.setState({ isPopoverOpen: false });
    }
    return (
      <Popover
        classNameBody="filter-popover"
        onRequestClose={onRequestClose}
        isOpen={isOpen}
      />
    );
  }

  onChangeFilter() {
    const propertySelection = this.propertySelection();
    const predicateSelection = this.predicateSelection();
    if (propertySelection.length && predicateSelection) {
      const property = propertySelection[0].id;
      const predicate = Array.isArray(predicateSelection)
        ? predicateSelection.length
          ? predicateSelection[0].id
          : undefined
        : predicateSelection;

      if (predicate) {
        this.props.onChange({
          property,
          predicate,
          type: propertySelection[0].type
        });
        this.setState({
          isPopoverOpen: false,
          comboboxInProgress: true,
          propertySelection: [],
          predicateSelection: undefined
        });
      }
    }
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { property, predicate } = this.props.filter;

    let resolvedProperty = property;
    if (this.optionForAttribute(property).type === 'required') {
      resolvedProperty = formatMessage({
        id: `components.ecs.assetFilter.${property}`
      });
    } else {
      const attributes = Object.entries(this.props.attributes).flatMap(
        e => e[1]
      );
      const resolvedAttribute = attributes.find(
        attr => `attribute.${attr.uuid}` === property
      );
      resolvedProperty = resolvedAttribute?.name;
    }

    let resolvedPredicate = predicate;
    if (property === 'start_datetime' || property === 'end_datetime') {
      const from = moment(predicate.startDate).format('YYYY-MM-DD');
      const to = moment(predicate.endDate).format('YYYY-MM-DD');

      resolvedPredicate = formatMessage(
        { id: 'components.ecs.assetFilter.datePredicate' },
        { start_date: from, end_date: to }
      );
    } else if (predicate && predicate === '(empty)') {
      resolvedPredicate = formatMessage({
        id: 'components.ecs.assetFilter.empty'
      });
    }

    return withFlags(flags => (
      <Filter
        property={resolvedProperty}
        predicate={resolvedPredicate}
        isPermanent={false}
        onClick={() => this.setState({ isPopoverOpen: true })}
        onChange={this.onChangeFilter.bind(this)}
        onRemove={this.props.onRemove}
        popover={this.popoverBody()}
      >
        {this.attributeCombobox(flags)}
        {this.attributeValueCombobox()}
      </Filter>
    ));
  }
}

AssetCustomFilter.propTypes = {
  customFilters: PropTypes.array,
  filter: PropTypes.object,
  attributes: PropTypes.object,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  inProgress: PropTypes.bool,
  location: PropTypes.object
};

export default injectIntl(AssetCustomFilter);
